import React, { useCallback, useRef } from 'react';

import { renderStreamingHtml } from '~/components/AIInsightsSidebar/AIInsightsSidebar.utils';
import { CitationMarker } from '~/components/AIInsightsSidebar/WebSearch/webSearch.types';

interface UseTypewriterParams {
  streamingBufferRef: React.MutableRefObject<string>;
  displayedLengthRef: React.MutableRefObject<number>;
  streamingMessageRef: React.MutableRefObject<HTMLDivElement | null>;
  scrollToBottom: () => void;
  shouldAnimateRef: React.MutableRefObject<boolean>;
  streamingCitationsRef: React.MutableRefObject<CitationMarker[]>;
  streamingPartIndexRef: React.MutableRefObject<number>;
}

const CHARS_PER_UPDATE = 5;
const UPDATE_INTERVAL = 32;

export const useTypewriter = ({
  streamingBufferRef,
  displayedLengthRef,
  streamingMessageRef,
  scrollToBottom,
  shouldAnimateRef,
  streamingCitationsRef,
  streamingPartIndexRef,
}: UseTypewriterParams) => {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isCancelledRef = useRef(false);

  const startTypewriter = useCallback(() => {
    isCancelledRef.current = false;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      if (isCancelledRef.current) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        return;
      }

      const buffer = streamingBufferRef.current;
      const currentDisplayedLength = displayedLengthRef.current;

      if (currentDisplayedLength < buffer.length && shouldAnimateRef.current) {
        const charsToAdd = Math.min(CHARS_PER_UPDATE, buffer.length - currentDisplayedLength);
        const newLength = currentDisplayedLength + charsToAdd;
        displayedLengthRef.current = newLength;

        if (streamingMessageRef.current) {
          const text = buffer.slice(0, newLength);
          streamingMessageRef.current.innerHTML = renderStreamingHtml(text, {
            citations: streamingCitationsRef.current,
            partIndex: streamingPartIndexRef.current,
            displayedLength: newLength,
          });
        }

        scrollToBottom();
      }
    }, UPDATE_INTERVAL);
  }, [
    streamingBufferRef,
    displayedLengthRef,
    streamingMessageRef,
    scrollToBottom,
    shouldAnimateRef,
    streamingCitationsRef,
    streamingPartIndexRef,
  ]);

  const stopTypewriter = useCallback(() => {
    isCancelledRef.current = true;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    displayedLengthRef.current = 0;
  }, [displayedLengthRef]);

  const waitForTypewriterCompletion = useCallback((): Promise<void> => {
    return new Promise<void>((resolve) => {
      const maxWaitTime = 30000;
      const startTime = Date.now();

      const checkComplete = () => {
        if (isCancelledRef.current) {
          resolve();
          return;
        }

        if (Date.now() - startTime > maxWaitTime) {
          resolve();
          return;
        }

        if (displayedLengthRef.current >= streamingBufferRef.current.length && streamingBufferRef.current.length > 0) {
          setTimeout(() => resolve(), 50);
        } else {
          setTimeout(checkComplete, 50);
        }
      };
      checkComplete();
    });
  }, [displayedLengthRef, streamingBufferRef]);

  return {
    startTypewriter,
    stopTypewriter,
    waitForTypewriterCompletion,
  };
};
